import type { SeatDetail } from './types'
import { CARD_WIDTH, seatCardHeight } from './cardMetrics'
import type { CardDensity } from './cardMetrics'

/**
 * Tidy Results Ownership Chart layout.
 *
 * Every card is the same width but a variable height (density + content), so
 * the tree is placed by hand rather than by a generic graph algorithm:
 *   - leaves take the next horizontal slot, left to right, in sibling order
 *   - a parent is centered over the span of its children
 *   - each depth is one row, tall enough for the tallest card in it
 *   - separate root trees are laid side by side on the same top row
 *
 * With a uniform card width a subtree always spans at least one slot, so a
 * centered parent can never overhang into a neighbouring subtree.
 *
 * The top row is centered over the whole chart through the reported canvas
 * rather than by moving the root cards. Both rules — "every parent sits over
 * its own children" and "the top row is centered over the chart" — cannot hold
 * in the cards' own bounding box once the outermost root branches differ in
 * width: the roots are pinned over their children, so the bounding box always
 * leans toward the wider outer branch. So the layout reports a canvas that is
 * symmetric about the top row (`x`/`width`, which the camera fits) and the
 * camera fits that canvas. Fitting the cards' bounding box instead is what made
 * a Visionary + Second in Command chart look packed toward one edge.
 *
 * Hand placement wins over all of it. A seat carrying stored coordinates is
 * drawn exactly there and is never shifted by this pass — a translation applied
 * after a drop would be saved back on the next drag and the box would creep
 * across the canvas one reload at a time. Only the tidy-placed cards are
 * normalized and padded; the reported canvas then stretches to cover both, so
 * it can start left of or above the origin.
 */

export const SIBLING_GAP = 48
export const LEVEL_GAP = 72
export const ROOT_TREE_GAP = 96

export interface PositionedNode {
  id: string
  seat: SeatDetail
  x: number
  y: number
  width: number
  height: number
}

export interface PositionedEdge {
  id: string
  source: string
  target: string
}

export interface ChartLayout {
  nodes: PositionedNode[]
  edges: PositionedEdge[]
  /** Top-left of the canvas the camera fits. Hand-placed cards can sit left of
   *  or above the tidy tree's origin, so this is not always 0. */
  x: number
  y: number
  /** Canvas the camera fits: symmetric about the top row, so it can be wider
   *  than the cards themselves. */
  width: number
  height: number
}

export interface LayoutOptions {
  /** Auto Layout preview: show the tidy tree as if nothing had been moved by
   *  hand. Nothing is saved until the preview is applied. */
  ignoreStoredPositions?: boolean
}

/** A box someone has dropped somewhere: the tidy tree no longer places it. */
export function hasStoredPosition(seat: SeatDetail): boolean {
  return seat.layout_x !== null && seat.layout_x !== undefined &&
    seat.layout_y !== null && seat.layout_y !== undefined
}

export function layoutChart(
  seats: SeatDetail[],
  collapsedIds: Set<number>,
  density: CardDensity,
  options: LayoutOptions = {},
): ChartLayout {
  const visible = filterVisible(seats, collapsedIds)
  if (visible.length === 0) return { nodes: [], edges: [], x: 0, y: 0, width: 0, height: 0 }

  // The branch toggle is driven by the full tree, not the visible slice, so a
  // collapsed branch keeps the strip that expands it again.
  const childCount = new Map<number, number>()
  seats.forEach(s => {
    if (s.parent_seat_id !== null) childCount.set(s.parent_seat_id, (childCount.get(s.parent_seat_id) || 0) + 1)
  })

  const visibleIds = new Set(visible.map(s => s.id))
  const childrenOf = new Map<number, SeatDetail[]>()
  const roots: SeatDetail[] = []
  for (const seat of visible) {
    const parentId = seat.parent_seat_id
    if (parentId !== null && visibleIds.has(parentId)) {
      const siblings = childrenOf.get(parentId) || []
      siblings.push(seat)
      childrenOf.set(parentId, siblings)
    } else {
      roots.push(seat)
    }
  }

  const heightOf = (seat: SeatDetail) =>
    seatCardHeight(seat, { density, hasChildren: (childCount.get(seat.id) || 0) > 0 })

  // Depth per visible seat, and the tallest card on each depth.
  const depthOf = new Map<number, number>()
  const rowHeights: number[] = []
  const assignDepth = (seat: SeatDetail, depth: number) => {
    depthOf.set(seat.id, depth)
    rowHeights[depth] = Math.max(rowHeights[depth] || 0, heightOf(seat))
    ;(childrenOf.get(seat.id) || []).forEach(child => assignDepth(child, depth + 1))
  }
  roots.forEach(root => assignDepth(root, 0))

  const rowY: number[] = []
  rowHeights.forEach((_rowHeight, depth) => {
    rowY[depth] = depth === 0 ? 0 : rowY[depth - 1] + rowHeights[depth - 1] + LEVEL_GAP
  })

  const centerX = new Map<number, number>()
  const subtreeWidth = new Map<number, number>()
  const measure = (seat: SeatDetail): number => {
    const children = childrenOf.get(seat.id) || []
    const widths = children.map(measure)
    // Equal direct-child center spacing, sized for the widest adjacent pair
    // of descendant envelopes. Each subtree is symmetric about its root.
    const step = widths.length > 1
      ? Math.max(...widths.slice(1).map((w, i) => (w + widths[i]) / 2 + SIBLING_GAP)) : 0
    const width = widths.length ? Math.max(CARD_WIDTH,
      step * (widths.length - 1) + (widths[0] + widths[widths.length - 1]) / 2) : CARD_WIDTH
    // Symmetric envelope also accounts for a wide off-center inner subtree.
    const envelope = widths.length ? Math.max(width, ...widths.map((w, i) =>
      2 * Math.abs((i - (widths.length - 1) / 2) * step) + w)) : width
    subtreeWidth.set(seat.id, envelope)
    return envelope
  }
  roots.forEach(measure)
  let cursor = 0
  const place = (seat: SeatDetail, center: number): void => {
    const children = childrenOf.get(seat.id) || []
    const widths = children.map(c => subtreeWidth.get(c.id)!)
    const step = widths.length > 1
      ? Math.max(...widths.slice(1).map((w, i) => (w + widths[i]) / 2 + SIBLING_GAP)) : 0
    children.forEach((child, i) => place(child, center + (i - (children.length - 1) / 2) * step))
    centerX.set(seat.id, center)
  }
  roots.forEach((root, index) => {
    if (index > 0) cursor += ROOT_TREE_GAP
    const width = subtreeWidth.get(root.id)!
    place(root, cursor + width / 2)
    cursor += width
  })

  const nodes: PositionedNode[] = visible.map(seat => {
    const depth = depthOf.get(seat.id) || 0
    const height = heightOf(seat)
    return {
      id: String(seat.id),
      seat,
      x: (centerX.get(seat.id) || 0) - CARD_WIDTH / 2,
      y: rowY[depth] || 0,
      width: CARD_WIDTH,
      height,
    }
  })

  // Normalizing and padding is the tidy tree's business only: a hand-placed
  // card is drawn at the coordinates that were saved for it, full stop.
  const placedByHand = (node: PositionedNode) =>
    !options.ignoreStoredPositions && hasStoredPosition(node.seat)
  const tidyNodes = nodes.filter(n => !placedByHand(n))

  if (tidyNodes.length > 0) {
    const minX = Math.min(...tidyNodes.map(n => n.x))
    if (minX !== 0) tidyNodes.forEach(n => { n.x -= minX })

    // Center the top row over the canvas by padding the narrow side. Every card
    // keeps the x its parent-over-children placement gave it.
    const cardsWidth = Math.max(...tidyNodes.map(n => n.x + n.width))
    const rootIds = new Set(roots.map(r => r.id))
    const tidyRootCenters = tidyNodes.filter(n => rootIds.has(n.seat.id)).map(n => n.x + n.width / 2)
    const tidyTopRowCenter = tidyRootCenters.length > 0
      ? (Math.min(...tidyRootCenters) + Math.max(...tidyRootCenters)) / 2
      : cardsWidth / 2
    const leftPad = Math.max(tidyTopRowCenter, cardsWidth - tidyTopRowCenter) - tidyTopRowCenter
    if (leftPad > 0) tidyNodes.forEach(n => { n.x += leftPad })
  }

  nodes.forEach(node => {
    if (!placedByHand(node)) return
    node.x = node.seat.layout_x as number
    node.y = node.seat.layout_y as number
  })

  const edges: PositionedEdge[] = visible
    .filter(s => s.parent_seat_id !== null && visibleIds.has(s.parent_seat_id))
    .map(s => ({ id: `e-${s.parent_seat_id}-${s.id}`, source: String(s.parent_seat_id), target: String(s.id) }))

  return { nodes, edges, ...canvasFor(nodes, new Set(roots.map(r => r.id))) }
}

/**
 * The region the camera frames: every card, tidy or hand-placed, inside a box
 * that is symmetric about the top row so the roots stay centered on screen.
 */
function canvasFor(nodes: PositionedNode[], rootIds: Set<number>) {
  const minX = Math.min(...nodes.map(n => n.x))
  const maxX = Math.max(...nodes.map(n => n.x + n.width))
  const minY = Math.min(...nodes.map(n => n.y))
  const maxY = Math.max(...nodes.map(n => n.y + n.height))

  const rootCenters = nodes.filter(n => rootIds.has(n.seat.id)).map(n => n.x + n.width / 2)
  const topRowCenter = rootCenters.length > 0
    ? (Math.min(...rootCenters) + Math.max(...rootCenters)) / 2
    : (minX + maxX) / 2
  const halfCanvas = Math.max(topRowCenter - minX, maxX - topRowCenter)

  return {
    x: topRowCenter - halfCanvas,
    y: minY,
    width: halfCanvas * 2,
    height: maxY - minY,
  }
}

/**
 * Where a box dropped ON another box comes to rest: just under its new parent,
 * stepping sideways until it is clear of everything already on the canvas.
 *
 * Releasing a card on top of another card means "report to this one", and
 * leaving it at the literal release point would park it over the very card it
 * was aimed at — the parent would vanish under its own child.
 */
export function placeUnderTarget(
  target: PositionedNode,
  nodes: PositionedNode[],
  movingSeatId: number,
): { x: number; y: number } {
  const others = nodes.filter(n => n.seat.id !== movingSeatId && n.seat.id !== target.seat.id)
  const moving = nodes.find(n => n.seat.id === movingSeatId)
  const height = moving?.height ?? target.height
  const y = target.y + target.height + LEVEL_GAP
  const step = CARD_WIDTH + SIBLING_GAP
  const clear = (x: number) => !others.some(n =>
    x < n.x + n.width && x + CARD_WIDTH > n.x && y < n.y + n.height && y + height > n.y)

  if (clear(target.x)) return { x: target.x, y }
  for (let i = 1; i <= 12; i++) {
    if (clear(target.x + i * step)) return { x: target.x + i * step, y }
    if (clear(target.x - i * step)) return { x: target.x - i * step, y }
  }
  return { x: target.x, y }
}

/** Seats reachable without opening a collapsed branch, in sibling order. */
export function filterVisible(seats: SeatDetail[], collapsedIds: Set<number>): SeatDetail[] {
  const byParent = new Map<number | null, SeatDetail[]>()
  for (const s of seats) {
    const arr = byParent.get(s.parent_seat_id) || []
    arr.push(s)
    byParent.set(s.parent_seat_id, arr)
  }
  const bySortOrder = (a: SeatDetail, b: SeatDetail) => a.position - b.position || a.id - b.id
  const roots = (byParent.get(null) || []).slice().sort(bySortOrder)
  const out: SeatDetail[] = []
  const walk = (s: SeatDetail) => {
    out.push(s)
    if (collapsedIds.has(s.id)) return
    ;(byParent.get(s.id) || []).slice().sort(bySortOrder).forEach(walk)
  }
  roots.forEach(walk)
  return out
}
